import { useState } from 'react';

import { Alert, Button, TextField, Typography } from '@/ui';

import {
  AssistantActionPage,
  AssistantCommandProblemAlert,
} from './AssistantActionPage';
import { useAssistantCommand } from './useAssistantCommand';

import type { AssistantActionContext } from './AssistantActionPage';
import type { FC } from 'react';

const LATE_CANCELLATION_MS = 48 * 60 * 60 * 1000;
const MAX_REASON_LENGTH = 1000;

/** Moments records a cancellation 48h or less before the start as late. */
export const isLateCancellation = (startAt: string, now = Date.now()) =>
  Date.parse(startAt) - now <= LATE_CANCELLATION_MS;

const CancelForm: FC<AssistantActionContext> = ({
  appointment,
  memberId,
  memberName,
}) => {
  const [reason, setReason] = useState('');
  const { isChecking, problem, submit } = useAssistantCommand({
    action: 'cancel',
    memberId,
    seen: appointment,
  });

  return (
    <div className="flex flex-col gap-4">
      <Typography variant="h5">Cancel {memberName}&apos;s session</Typography>
      <Typography color="text.secondary" variant="body2">
        Only this session is cancelled; the rest of a recurring series stays as
        it is.
      </Typography>
      {isLateCancellation(appointment.startAt) ? (
        <Alert severity="warning">
          This is 48 hours or less before the start, so it counts as a late
          cancellation for {memberName}, as it would if they cancelled it
          themselves.
        </Alert>
      ) : null}
      <TextField
        inputProps={{ maxLength: MAX_REASON_LENGTH }}
        label="Reason (optional)"
        minRows={2}
        value={reason}
        fullWidth
        multiline
        onChange={(event) => setReason(event.target.value)}
      />
      <AssistantCommandProblemAlert action="cancel" problem={problem} />
      <div>
        <Button
          color="error"
          disabled={isChecking}
          variant="contained"
          onClick={() => {
            submit({ reason: reason.trim() || null });
          }}
        >
          Cancel this session
        </Button>
      </div>
    </div>
  );
};

/** `/assistant/members/:memberId/appointments/:appointmentId/cancel` */
const AssistantCancel: FC = () => (
  <AssistantActionPage action="cancel">
    {(context) => <CancelForm {...context} />}
  </AssistantActionPage>
);

export default AssistantCancel;
